import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import Select from "react-select";
import countryList from "react-select-country-list";

import "../styles/profile.css";
import "../styles/hrPageShell.css";
import { useMsal } from "@azure/msal-react";
import { getEmployeeMasterByEmail, getEmployeeMasters } from "../services/employeeService";
import { getCurrentEmployeeCTC, getEmployeeCTCHistory } from "../services/ctcService";
import { displayValue } from "../utils/displayValue";
import { getCurrentUser } from "../utils/userContext";

// =====================================================
// ✅ PHASE 3A CONFIG - Profile Completion + Submit Workflow
// =====================================================
// Replace this placeholder with your new Power Automate Submit_Profile HTTP URL.
const SUBMIT_PROFILE_FLOW_URL = "/tracka-flow/v1/SUBMIT_PROFILE";

const PROFILE_COMPLETION_STATUS = {
  NOT_STARTED: "Not Started",
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  SUBMITTED: "Submitted",
  RETURNED_FOR_CORRECTION: "Returned for Correction"
};

const PROFILE_VERIFICATION_STATUS = {
  NOT_VERIFIED: "Not Verified",
  PENDING_VERIFICATION: "Pending Verification",
  VERIFIED: "Verified",
  REJECTED: "Rejected"
};

const ONBOARDING_STAGE = {
  OFFERED: "Offered",
  PREBOARDING: "Preboarding",
  PROFILE_PENDING: "Profile Pending",
  ONBOARDING: "Onboarding",
  ACTIVE: "Active"
};

function normalizeCTCText(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "object") {
    if (Array.isArray(value)) return value.map(normalizeCTCText).filter(Boolean).join("; ");
    return normalizeCTCText(value.Value ?? value.value ?? value.Title ?? value.Email ?? value.DisplayName ?? value.Name ?? value.Label ?? "");
  }
  const text = String(value).trim();
  if (!text) return "";
  if ((text.startsWith("{") && text.endsWith("}")) || (text.startsWith("[") && text.endsWith("]"))) {
    try { return normalizeCTCText(JSON.parse(text)); } catch { return text; }
  }
  return text;
}

function getCTCField(record, ...candidateNames) {
  if (!record || typeof record !== "object") return "";
  for (const name of candidateNames) {
    if (record[name] !== undefined && record[name] !== null) return record[name];
  }
  const lookup = Object.keys(record).reduce((acc, key) => {
    acc[String(key).toLowerCase().replace(/[^a-z0-9]/g, "")] = record[key];
    return acc;
  }, {});
  for (const name of candidateNames) {
    const normalizedName = String(name).toLowerCase().replace(/[^a-z0-9]/g, "");
    if (lookup[normalizedName] !== undefined && lookup[normalizedName] !== null) return lookup[normalizedName];
  }
  return "";
}

function toCTCNumber(value) {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const text = normalizeCTCText(value).replace(/[^0-9.-]/g, "");
  if (!text) return null;
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
}

function formatCTCMoney(value, currency = "INR") {
  const number = toCTCNumber(value);
  if (number === null) return normalizeCTCText(value) || "-";
  const cleanCurrency = normalizeCTCText(currency) || "INR";
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: cleanCurrency, maximumFractionDigits: 0 }).format(number);
  } catch {
    return `${cleanCurrency} ${number.toLocaleString("en-IN")}`;
  }
}

function formatCTCDate(value) {
  const cleanValue = normalizeCTCText(value);
  if (!cleanValue) return "-";
  const date = new Date(cleanValue);
  if (Number.isNaN(date.getTime())) return cleanValue;
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function parseComponentBreakupJson(value) {
  if (!value) return [];
  try {
    const parsed = typeof value === "string" ? JSON.parse(value || "[]") : value;
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function Profile() {

  const countries = useMemo(() => countryList().getData(), []);
  const { accounts } = useMsal();
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileLoadError, setProfileLoadError] = useState("");

  const imageInputRef = useRef(null);

  const [editMode, setEditMode] = useState(false);

  const [profileImage, setProfileImage] = useState(null);

  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);

  const [resumeFile, setResumeFile] = useState(null);
  const [extractingResume, setExtractingResume] = useState(false);
  const [extractedResumeData, setExtractedResumeData] = useState(null);
  const [showExtractReviewModal, setShowExtractReviewModal] = useState(false);

  const [errors, setErrors] = useState({});
  const [myCTC, setMyCTC] = useState(null);
  const [myCTCHistory, setMyCTCHistory] = useState([]);
  const [loadingMyCTC, setLoadingMyCTC] = useState(false);
  const [myCTCError, setMyCTCError] = useState("");
  const [showMyCTCHistory, setShowMyCTCHistory] = useState(false);
  const [showMyCTCPanel, setShowMyCTCPanel] = useState(false);

  const [profileData, setProfileData] = useState({

    // =====================================================
    // ✅ IDENTITY
    // =====================================================

    employeeId: "",
    workEmail: "",

    department: "",
    designation: "",
    gradeLevel: "",
    reportingManager: "",
    location: "",
    joiningDate: "",
    employmentStatus: "",

    // =====================================================
    // ✅ PERSONAL
    // =====================================================

    firstName: "",
    lastName: "",
    fullName: "",

    mobile: "",

    personalEmail: "",
    altEmail: "",

    dob: "",
    gender: "",
    nationality: "",

    // =====================================================
    // ✅ ADDRESS
    // =====================================================

    address1: "",
    address2: "",

    postalCode: "",
    city: "",
    state: "",

    country: null,

    // =====================================================
    // ✅ EMERGENCY
    // =====================================================

    
    bloodGroup: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    relationship: "",
    familyDoctorDetails: "",
    medicalHistory: "",

    // =====================================================
    // ✅ QUALIFICATION
    // =====================================================

    qualification: "",
    institute: "",
    graduationYear: 0,

    experience: 0,
    domain: "",
    skills: "",

    lastDesignation: "",
    lastCompany: "",
    lastRelievingDate: "",
    
    qualificationSummary: "",
    experienceSummary: "",

    achievements: "",
    certifications: "",
    trainingsCompleted: "",

    awards: "",
    performanceSummary: "",

    // =====================================================
    // ✅ LINKS
    // =====================================================

    linkedin: "",
    github: "",
    portfolio: "",

    // =====================================================
    // ✅ ACCOUNT
    // =====================================================

    bankName: "",
    accountHolder: "",
    accountNumber: "",

    ifsc: "",
    iban: "",
    swift: "",

    // =====================================================
    // ✅ COMMUNITY
    // =====================================================

    community: "",

    visibility: "Me + HR + Manager",

    profilePhotoLink: "",
    resumeLink: "",
    attachmentsLink: "",

    // =====================================================
    // ✅ EMPLOYMENT / ORGANIZATION - READ ONLY FROM EMPLOYEEMASTER
    // =====================================================
    legalEntity: "",
    legalEntityCode: "",
    countryCode: "",
    regionState: "",
    locationCode: "",
    workLocationId: "",
    departmentCode: "",
    workUnitCode: "",
    productionUnitCode: "",
    managerEmployeeCode: "",
    industryType: "",
    employmentType: "",
    workArrangement: "",
    ftePercent: "",
    timeZone: "",
    leavePolicyId: "",
    attendancePolicyId: "",
    shiftPatternId: "",
    shiftPolicyId: "",
    weeklyOffPatternId: "",
    holidayCalendarId: "",
    payGrade: "",
    salaryCurrency: "",
    compensationStatus: "",
    currentEmployeeCTCId: "",
    currentCTCStructureId: "",
    currentCTCEffectiveFrom: "",
    currentCTCStatus: "",
    annualCTC: "",
    monthlyCTC: "",
    monthlyGross: "",
    annualEmployerCost: "",
    netPayEstimateMonthly: "",
    cTCLastApprovedOn: "",
    cTCRevisionNumber: "",
    revisionDueFlag: false,
    profileReviewComments: "",
    profileCorrectionComments: "",
    correctionComments: "",
    verificationComments: "",

    // =====================================================
    // ✅ PHASE 3A WORKFLOW STATUS
    // =====================================================
    profileCompletionStatus: PROFILE_COMPLETION_STATUS.NOT_STARTED,
    profileVerificationStatus: PROFILE_VERIFICATION_STATUS.NOT_VERIFIED,
    onboardingStage: ONBOARDING_STAGE.PREBOARDING

  });


  const toCountryOption = useCallback(
  (countryValue) => {
    const text = displayValue(countryValue);

    if (!text) return null;

    return (
      countries.find(
        (country) => country.label === text || country.value === text
      ) || {
        label: text,
        value: text
      }
    );
    },
    [countries]
   );
  
   const getSharePointImageSrc = (url) => {
      if (!url) return "";

      const cleanUrl = String(url).trim();

      // Convert SharePoint preview link → direct file
      if (cleanUrl.includes("sharepoint.com") && !cleanUrl.includes("download=1")) {
        return cleanUrl + (cleanUrl.includes("?") ? "&" : "?") + "download=1";
      }

      return cleanUrl;
    };



  // =====================================================
  // ✅ PHASE 3A - COMPLETION + LOCKING HELPERS
  // =====================================================
    const hasText = (value) => String(value || "").trim().length > 0;

    const normalizeStatus = useCallback((value) => {
      return String(value || "").trim().toLowerCase();
    }, []);

    const isSameStatus = useCallback((value, expected) => {
      return normalizeStatus(value) === normalizeStatus(expected);
    }, [normalizeStatus]);

    const canonicalCompletionStatus = useCallback((value) => {
      if (isSameStatus(value, PROFILE_COMPLETION_STATUS.SUBMITTED)) {
        return PROFILE_COMPLETION_STATUS.SUBMITTED;
      }

      if (isSameStatus(value, PROFILE_COMPLETION_STATUS.RETURNED_FOR_CORRECTION)) {
        return PROFILE_COMPLETION_STATUS.RETURNED_FOR_CORRECTION;
      }

      if (isSameStatus(value, PROFILE_COMPLETION_STATUS.COMPLETED)) {
        return PROFILE_COMPLETION_STATUS.COMPLETED;
      }

      if (isSameStatus(value, PROFILE_COMPLETION_STATUS.IN_PROGRESS)) {
        return PROFILE_COMPLETION_STATUS.IN_PROGRESS;
      }

      return PROFILE_COMPLETION_STATUS.NOT_STARTED;
    }, [isSameStatus]);

    const canonicalVerificationStatus = useCallback((value) => {
      if (
        isSameStatus(value, PROFILE_VERIFICATION_STATUS.PENDING_VERIFICATION) ||
        isSameStatus(value, "Pending HR Review")
      ) {
        return PROFILE_VERIFICATION_STATUS.PENDING_VERIFICATION;
      }

      if (isSameStatus(value, PROFILE_VERIFICATION_STATUS.VERIFIED)) {
        return PROFILE_VERIFICATION_STATUS.VERIFIED;
      }

      if (
        isSameStatus(value, PROFILE_VERIFICATION_STATUS.REJECTED) ||
        isSameStatus(value, "Correction Required")
      ) {
        return PROFILE_VERIFICATION_STATUS.REJECTED;
      }

      return PROFILE_VERIFICATION_STATUS.NOT_VERIFIED;
    }, [isSameStatus]);

  const calculateProfileCompletion = useCallback((data) => {
    let score = 0;

    if (hasText(data.firstName) && hasText(data.lastName) && hasText(data.mobile) && hasText(data.dob)) score += 20;
    if (hasText(data.postalCode) && hasText(data.city) && Boolean(data.country)) score += 20;
    if (hasText(data.qualification) && Number(data.experience) > 0 && hasText(data.skills)) score += 20;
    if (hasText(data.emergencyContactName) && hasText(data.emergencyContactPhone) && hasText(data.relationship)) score += 20;
    if (hasText(data.profilePhotoLink) && hasText(data.resumeLink)) score += 20;

    return score;
  }, []);

  const completionPercentage = useMemo(
    () => calculateProfileCompletion(profileData),
    [calculateProfileCompletion, profileData]
  );

  const getAutoCompletionStatus = useCallback((data, completion) => {
    const currentStatus = canonicalCompletionStatus(data.profileCompletionStatus);
    const currentVerification = canonicalVerificationStatus(data.profileVerificationStatus);

    // Once submitted, keep it submitted until HR action.
    if (currentStatus === PROFILE_COMPLETION_STATUS.SUBMITTED) return PROFILE_COMPLETION_STATUS.SUBMITTED;

    // If HR returned the profile, keep correction state until employee edits/completes again.
    if (
      currentStatus === PROFILE_COMPLETION_STATUS.RETURNED_FOR_CORRECTION ||
      currentVerification === PROFILE_VERIFICATION_STATUS.REJECTED
    ) {
      return completion === 100
        ? PROFILE_COMPLETION_STATUS.COMPLETED
        : PROFILE_COMPLETION_STATUS.RETURNED_FOR_CORRECTION;
    }

    if (completion === 100) return PROFILE_COMPLETION_STATUS.COMPLETED;
    if (completion > 0) return PROFILE_COMPLETION_STATUS.IN_PROGRESS;
    return PROFILE_COMPLETION_STATUS.NOT_STARTED;
  }, [canonicalCompletionStatus, canonicalVerificationStatus]);

  const effectiveProfileCompletionStatus = useMemo(
    () => getAutoCompletionStatus(profileData, completionPercentage),
    [getAutoCompletionStatus, profileData, completionPercentage]
  );

  const currentCompletionStatus = canonicalCompletionStatus(profileData.profileCompletionStatus);
  const currentVerificationStatus = canonicalVerificationStatus(profileData.profileVerificationStatus);

  const isProfileSubmitted = currentCompletionStatus === PROFILE_COMPLETION_STATUS.SUBMITTED;
  const isVerificationPending = currentVerificationStatus === PROFILE_VERIFICATION_STATUS.PENDING_VERIFICATION;
  const isVerificationVerified = currentVerificationStatus === PROFILE_VERIFICATION_STATUS.VERIFIED;
  const isVerificationRejected = currentVerificationStatus === PROFILE_VERIFICATION_STATUS.REJECTED;
  const displayOnboardingStage =
    isProfileSubmitted &&
    (currentVerificationStatus === PROFILE_VERIFICATION_STATUS.NOT_VERIFIED ||
      currentVerificationStatus === PROFILE_VERIFICATION_STATUS.PENDING_VERIFICATION)
      ? ONBOARDING_STAGE.PROFILE_PENDING
      : (profileData.onboardingStage || ONBOARDING_STAGE.PREBOARDING);

  // Submitted profile must remain locked even if SharePoint temporarily stores verification as Not Verified.
  const isProfileLocked = isProfileSubmitted || isVerificationPending || isVerificationVerified;

  const canEditProfile = editMode && !isProfileLocked;

  const canSubmitProfile =
    completionPercentage === 100 &&
    !editMode &&
    !isProfileLocked &&
    !isProfileSubmitted;

  const getEditStatusPatch = (prev) => {
    const previousVerification = canonicalVerificationStatus(prev.profileVerificationStatus);

    return {
      profileCompletionStatus:
        previousVerification === PROFILE_VERIFICATION_STATUS.REJECTED
          ? PROFILE_COMPLETION_STATUS.IN_PROGRESS
          : canonicalCompletionStatus(prev.profileCompletionStatus) === PROFILE_COMPLETION_STATUS.SUBMITTED
            ? PROFILE_COMPLETION_STATUS.SUBMITTED
            : PROFILE_COMPLETION_STATUS.IN_PROGRESS,
      profileVerificationStatus:
        previousVerification === PROFILE_VERIFICATION_STATUS.REJECTED
          ? PROFILE_VERIFICATION_STATUS.NOT_VERIFIED
          : canonicalVerificationStatus(prev.profileVerificationStatus),
      onboardingStage:
        previousVerification === PROFILE_VERIFICATION_STATUS.REJECTED
          ? ONBOARDING_STAGE.PROFILE_PENDING
          : prev.onboardingStage
    };
  };

  const getStatusBadgeStyle = (statusType) => {
    const baseStyle = {
      display: "inline-flex",
      alignItems: "center",
      borderRadius: "999px",
      padding: "6px 12px",
      fontSize: "13px",
      fontWeight: 700,
      border: "1px solid transparent"
    };

    if (statusType === "success") return { ...baseStyle, color: "#047857", backgroundColor: "#dcfce7", borderColor: "#bbf7d0" };
    if (statusType === "warning") return { ...baseStyle, color: "#92400e", backgroundColor: "#fef3c7", borderColor: "#fde68a" };
    if (statusType === "danger") return { ...baseStyle, color: "#b91c1c", backgroundColor: "#fee2e2", borderColor: "#fecaca" };
    return { ...baseStyle, color: "#075985", backgroundColor: "#e0f2fe", borderColor: "#bae6fd" };
  };

  const verificationBadgeType =
    currentVerificationStatus === PROFILE_VERIFICATION_STATUS.VERIFIED
      ? "success"
      : currentVerificationStatus === PROFILE_VERIFICATION_STATUS.REJECTED
        ? "danger"
        : currentVerificationStatus === PROFILE_VERIFICATION_STATUS.PENDING_VERIFICATION
          ? "warning"
          : "info";
  const returnCorrectionComments = useMemo(() => {
    return displayValue(
      profileData.profileCorrectionComments ||
      profileData.correctionComments ||
      profileData.profileReviewComments ||
      profileData.verificationComments ||
      ""
    );
  }, [profileData.profileCorrectionComments, profileData.correctionComments, profileData.profileReviewComments, profileData.verificationComments]);
  const isEmployeeActive =
    displayOnboardingStage === ONBOARDING_STAGE.ACTIVE ||
    isSameStatus(profileData.employmentStatus, ONBOARDING_STAGE.ACTIVE);

  const profileStatusMessage =
    currentVerificationStatus === PROFILE_VERIFICATION_STATUS.VERIFIED && isEmployeeActive
      ? "Onboarding completed. Employee is active."
      : currentVerificationStatus === PROFILE_VERIFICATION_STATUS.PENDING_VERIFICATION
        ? "Profile is read-only while HR verification is in progress."
        : currentVerificationStatus === PROFILE_VERIFICATION_STATUS.REJECTED
          ? "Profile returned for correction. Please update and resubmit."
          : "";

  const profileStatusMessageType =
    currentVerificationStatus === PROFILE_VERIFICATION_STATUS.VERIFIED
      ? "success"
      : currentVerificationStatus === PROFILE_VERIFICATION_STATUS.REJECTED
        ? "danger"
        : "warning";


  // =====================================================
  // ✅ FIELD RESOLUTION HELPERS
  // Supports lowercase, PascalCase, and SharePoint Choice value objects
  // =====================================================
  const getEmployeeField = useCallback((employee, ...keys) => {
    if (!employee) return "";

    for (const key of keys) {
      const value = employee[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        if (typeof value === "object") {
          return value.Value || value.value || value.label || value.Label || "";
        }
        return value;
      }
    }

    const employeeKeys = Object.keys(employee);
    for (const key of keys) {
      const foundKey = employeeKeys.find(
        (employeeKey) => employeeKey.toLowerCase() === String(key).toLowerCase()
      );

      if (foundKey) {
        const value = employee[foundKey];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          if (typeof value === "object") {
            return value.Value || value.value || value.label || value.Label || "";
          }
          return value;
        }
      }
    }

    return "";
  }, []);

  const getWorkflowStatusFromEmployee = useCallback((employee) => {
    const completionStatus = canonicalCompletionStatus(
      getEmployeeField(
        employee,
        "profileCompletionStatus",
        "ProfileCompletionStatus",
        "profileCompletionStatusValue",
        "ProfileCompletionStatusValue"
      )
    );

    const verificationStatus = canonicalVerificationStatus(
      getEmployeeField(
        employee,
        "profileVerificationStatus",
        "ProfileVerificationStatus",
        "profileVerificationStatusValue",
        "ProfileVerificationStatusValue"
      )
    );

    const rawStage = displayValue(
      getEmployeeField(
        employee,
        "onboardingStage",
        "OnboardingStage",
        "onboardingStageValue",
        "OnboardingStageValue"
      )
    );

    let onboardingStage = rawStage || ONBOARDING_STAGE.PREBOARDING;

    if (
      completionStatus === PROFILE_COMPLETION_STATUS.SUBMITTED &&
      (verificationStatus === PROFILE_VERIFICATION_STATUS.NOT_VERIFIED ||
        verificationStatus === PROFILE_VERIFICATION_STATUS.PENDING_VERIFICATION)
    ) {
      onboardingStage = ONBOARDING_STAGE.PROFILE_PENDING;
    }

    if (verificationStatus === PROFILE_VERIFICATION_STATUS.REJECTED) {
      onboardingStage = ONBOARDING_STAGE.PROFILE_PENDING;
    }

    return {
      profileCompletionStatus: completionStatus,
      profileVerificationStatus: verificationStatus,
      onboardingStage
    };
  }, [getEmployeeField, canonicalCompletionStatus, canonicalVerificationStatus]);

  const getEmployeeEmail = useCallback((employee) => {
    return displayValue(getEmployeeField(employee, "workEmail", "WorkEmail", "email", "Email")).toLowerCase();
  }, [getEmployeeField]);

  const getEmployeeCode = useCallback((employee) => {
    return displayValue(getEmployeeField(employee, "employeeId", "EmpId", "EmployeeId", "EmployeeCode", "Title")).toLowerCase();
  }, [getEmployeeField]);

  const mergeEmployeeMasterRows = useCallback((primaryEmployee = {}, masterRow = {}) => {
    // Important for Returned-for-Correction flow:
    // Some GetByEmail/GetAll responses return blank strings for fields that exist in the other response.
    // Do not let blank values from one response overwrite previously saved EmployeeMaster/Profile values.
    const merged = { ...(masterRow || {}) };

    Object.entries(primaryEmployee || {}).forEach(([key, value]) => {
      const hasUsableValue =
        value === true ||
        value === false ||
        typeof value === "number" ||
        displayValue(value).trim() !== "";
      if (hasUsableValue) {
        merged[key] = value;
      }
    });

    const backfillFields = [
      "employeeId", "EmpId", "EmployeeId", "EmployeeCode", "Title", "workEmail", "WorkEmail",
      "firstName", "FirstName", "lastName", "LastName", "fullName", "FullName",
      "mobile", "Mobile", "mobileNumber", "MobileNumber", "phone", "Phone", "personalEmail", "PersonalEmail", "personalMail", "PersonalMail", "altEmail", "AlternateEmails", "AlternativeEmail",
      "dob", "dateOfBirth", "DOB", "DateOfBirth", "gender", "Gender", "nationality", "Nationality",
      "address1", "addressLine1", "AddressLine1", "address2", "addressLine2", "AddressLine2", "postalCode", "PostalCode", "city", "City", "state", "State",
      "country", "Country", "countryValue", "CountryValue", "countryCode", "CountryCode",
      "bloodGroup", "BloodGroup", "emergencyContactName", "EmergencyContactName",
      "emergencyContactPhone", "emergencyContactNumber", "EmergencyContactNumber", "relationship", "relation", "Relation",
      "familyDoctorDetails", "familyDocotorName", "FamilyDoctorDetails", "medicalHistory", "familyDoctorContact", "MedicalHistory",
      "qualification", "highestQualification", "HighestQualification", "institute", "Institute", "university", "University", "graduationYear", "GraduationYear",
      "experience", "Experience", "domain", "Domain", "skills", "Skilla", "Skills",
      "lastDesignation", "CurrentDesignation", "LastDesignation", "lastCompany", "LastCompany", "lastRelievingDate", "LastRelievingDate",
      "qualificationSummary", "QualificationSummary", "experienceSummary", "ExperienceSummary", "achievements", "Achievements",
      "certifications", "Certifications", "trainingsCompleted", "TrainingsCompleted", "awards", "Awards", "performanceSummary", "PerformanceSummary",
      "linkedin", "LinkedIn", "github", "GitHub", "portfolio", "Portfolio",
      "bankName", "BankName", "accountHolder", "AccountHolder", "accountNumber", "Bank_AccountNumberMasked", "BankAccountNumberMasked",
      "ifsc", "Bank_IFSC", "BankIFSC", "iban", "Bank_IBAN", "BankIBAN", "swift", "Swift", "SWIFT",
      "community", "Community", "visibility", "Visibility", "profilePhotoLink", "ProfilePhotoLink", "resumeLink", "ResumeLink",
      "attachmentsLink", "SharePointFolderPath", "AttachmentsLink",
      "legalEntity", "LegalEntity", "legalEntityName", "LegalEntityName", "legalEntityCode", "LegalEntityCode",
      "location", "Location", "locationName", "LocationName", "locationCode", "LocationCode", "workLocationId", "WorkLocationId",
      "regionState", "RegionState", "industryType", "IndustryType",
      "department", "Department", "departmentName", "DepartmentName", "departmentCode", "DepartmentCode",
      "workUnitCode", "WorkUnitCode", "productionUnitCode", "ProductionUnitCode",
      "designation", "Designation", "gradeLevel", "GradeLevel",
      "reportingManager", "ReportingManager", "reportManger", "managerEmployeeCode", "ManagerEmployeeCode",
      "joiningDate", "JoiningDate", "employmentStatus", "EmploymentStatus", "Status",
      "employmentType", "EmploymentType", "workArrangement", "WorkArrangement", "ftePercent", "FTEPercent", "timeZone", "TimeZone",
      "leavePolicyId", "LeavePolicyId", "attendancePolicyId", "AttendancePolicyId",
      "shiftPatternId", "ShiftPatternId", "shiftPolicyId", "ShiftPolicyId",
      "weeklyOffPatternId", "WeeklyOffPatternId", "holidayCalendarId", "HolidayCalendarId",
      "payGrade", "PayGrade", "salaryCurrency", "SalaryCurrency", "compensationStatus", "CompensationStatus",
      "currentEmployeeCTCId", "CurrentEmployeeCTCId", "currentCTCStructureId", "CurrentCTCStructureId",
      "currentCTCEffectiveFrom", "CurrentCTCEffectiveFrom", "currentCTCStatus", "CurrentCTCStatus",
      "annualCTC", "AnnualCTC", "monthlyCTC", "MonthlyCTC", "monthlyGross", "MonthlyGross",
      "annualEmployerCost", "AnnualEmployerCost", "netPayEstimateMonthly", "NetPayEstimateMonthly",
      "cTCLastApprovedOn", "CTCLastApprovedOn", "cTCRevisionNumber", "CTCRevisionNumber", "revisionDueFlag", "RevisionDueFlag",
      "profileCompletionStatus", "ProfileCompletionStatus", "profileVerificationStatus", "ProfileVerificationStatus", "onboardingStage", "OnboardingStage",
      "profileReviewComments", "ProfileReviewComments", "profileCorrectionComments", "ProfileCorrectionComments", "verificationComments", "VerificationComments",
      "correctionComments", "CorrectionComments", "returnComments", "ReturnComments",
      "reviewComments", "ReviewComments", "comments", "Comments", "rejectionReason", "RejectionReason", "profileVerificationRemarks", "ProfileVerificationRemarks"
    ];

    backfillFields.forEach((field) => {
      const current = displayValue(merged[field]);
      const fallbackFromPrimary = primaryEmployee ? primaryEmployee[field] : "";
      const fallbackFromMaster = masterRow ? masterRow[field] : "";
      if (!current && fallbackFromPrimary !== undefined && fallbackFromPrimary !== null && displayValue(fallbackFromPrimary).trim() !== "") {
        merged[field] = fallbackFromPrimary;
      } else if (!current && fallbackFromMaster !== undefined && fallbackFromMaster !== null && displayValue(fallbackFromMaster).trim() !== "") {
        merged[field] = fallbackFromMaster;
      }
    });
    return merged;
  }, []);

  const applyEmployeeRecordToProfile = useCallback((employee) => {
    if (!employee) return;

    const pick = (...keys) => displayValue(getEmployeeField(employee, ...keys));
    const keep = (previousValue, ...keys) => pick(...keys) || previousValue || "";
    const workflowStatus = getWorkflowStatusFromEmployee(employee);
    const employeeCountry = pick("country", "Country", "countryValue", "CountryValue", "countryName", "CountryName") || pick("countryCode", "CountryCode");
    const employeeCode = pick("employeeId", "EmpId", "EmployeeId", "EmployeeCode", "Title");
    const employeeName = pick("fullName", "FullName") || [pick("firstName", "FirstName"), pick("lastName", "LastName")].filter(Boolean).join(" ");

    setProfileData((prev) => ({
      ...prev,

      // Identity / basic employment
      employeeId: employeeCode,
      workEmail: pick("workEmail", "WorkEmail"),
      department: pick("department", "Department", "departmentName", "DepartmentName", "departmentCode", "DepartmentCode"),
      designation: pick("designation", "Designation"),
      gradeLevel: pick("gradeLevel", "GradeLevel"),
      reportingManager: pick("reportingManager", "ReportingManager", "reportManger"),
      location: pick("location", "Location", "locationName", "LocationName", "workLocationId", "WorkLocationId"),
      joiningDate: pick("joiningDate", "JoiningDate"),
      employmentStatus: pick("employmentStatus", "EmploymentStatus", "Status"),

      // Personal
      firstName: keep(prev.firstName, "firstName", "FirstName"),
      lastName: keep(prev.lastName, "lastName", "LastName"),
      fullName: employeeName || prev.fullName,
      mobile: keep(prev.mobile, "mobile", "Mobile", "mobileNumber", "MobileNumber", "phone", "Phone"),
      personalEmail: keep(prev.personalEmail, "personalEmail", "PersonalEmail", "personalMail", "PersonalMail"),
      altEmail: keep(prev.altEmail, "altEmail", "AlternateEmails", "AlternativeEmail"),
      dob: keep(prev.dob, "dob", "dateOfBirth", "DateOfBirth", "DOB"),
      gender: keep(prev.gender, "gender", "Gender"),
      nationality: keep(prev.nationality, "nationality", "Nationality"),

      // Address
      address1: keep(prev.address1, "address1", "addressLine1", "AddressLine1"),
      address2: keep(prev.address2, "address2", "addressLine2", "AddressLine2"),
      postalCode: keep(prev.postalCode, "postalCode", "PostalCode"),
      city: keep(prev.city, "city", "City"),
      state: keep(prev.state, "state", "State"),
      country: toCountryOption(employeeCountry) || prev.country,

      // Emergency / medical
      bloodGroup: keep(prev.bloodGroup, "bloodGroup", "BloodGroup"),
      emergencyContactName: keep(prev.emergencyContactName, "emergencyContactName", "EmergencyContactName"),
      emergencyContactPhone: keep(prev.emergencyContactPhone, "emergencyContactPhone", "emergencyContactNumber", "EmergencyContactNumber"),
      relationship: keep(prev.relationship, "relationship", "relation", "Relation"),
      familyDoctorDetails: keep(prev.familyDoctorDetails, "familyDoctorDetails", "familyDocotorName", "FamilyDoctorDetails"),
      medicalHistory: keep(prev.medicalHistory, "medicalHistory", "familyDoctorContact", "MedicalHistory"),

      // Qualification / experience
      qualification: keep(prev.qualification, "qualification", "highestQualification", "HighestQualification"),
      institute: keep(prev.institute, "institute", "Institute", "university", "University"),
      graduationYear: Number(pick("graduationYear", "GraduationYear", "yearOfGraduation", "YearOfGraduation")) || prev.graduationYear || 0,
      experience: Number(pick("experience", "Experience", "totalExperience", "TotalExperience")) || prev.experience || 0,
      domain: keep(prev.domain, "domain", "Domain"),
      skills: keep(prev.skills, "skills", "Skilla", "Skills", "skill", "Skill"),
      lastDesignation: keep(prev.lastDesignation, "lastDesignation", "CurrentDesignation", "LastDesignation"),
      lastCompany: keep(prev.lastCompany, "lastCompany", "LastCompany"),
      lastRelievingDate: keep(prev.lastRelievingDate, "lastRelievingDate", "LastRelievingDate"),
      qualificationSummary: keep(prev.qualificationSummary, "qualificationSummary", "QualificationSummary"),
      experienceSummary: keep(prev.experienceSummary, "experienceSummary", "ExperienceSummary"),
      achievements: keep(prev.achievements, "achievements", "Achievements"),
      certifications: keep(prev.certifications, "certifications", "Certifications"),
      trainingsCompleted: pick("trainingsCompleted", "TrainingsCompleted"),
      awards: pick("awards", "Awards"),
      performanceSummary: pick("performanceSummary", "PerformanceSummary"),

      // Links / bank / community
      linkedin: keep(prev.linkedin, "linkedin", "LinkedIn"),
      github: keep(prev.github, "github", "GitHub"),
      portfolio: keep(prev.portfolio, "portfolio", "Portfolio"),
      bankName: pick("bankName", "BankName"),
      accountHolder: pick("accountHolder", "AccountHolder"),
      accountNumber: pick("accountNumber", "Bank_AccountNumberMasked", "BankAccountNumberMasked"),
      ifsc: pick("ifsc", "Bank_IFSC", "BankIFSC"),
      iban: pick("iban", "Bank_IBAN", "BankIBAN"),
      swift: pick("swift", "Swift", "SWIFT"),
      community: pick("community", "Community"),
      visibility: pick("visibility", "Visibility") || prev.visibility,
      profilePhotoLink: keep(prev.profilePhotoLink, "profilePhotoLink", "ProfilePhotoLink"),
      resumeLink: keep(prev.resumeLink, "resumeLink", "ResumeLink"),
      attachmentsLink: keep(prev.attachmentsLink, "attachmentsLink", "SharePointFolderPath", "AttachmentsLink"),

      // Employment / organization / policy - read only from EmployeeMaster
      legalEntity: pick("legalEntity", "LegalEntity", "legalEntityName", "LegalEntityName"),
      legalEntityCode: pick("legalEntityCode", "LegalEntityCode", "LegalEntityId", "legalEntityId"),
      countryCode: pick("countryCode", "CountryCode"),
      regionState: pick("regionState", "RegionState"),
      locationCode: pick("locationCode", "LocationCode", "workLocationId", "WorkLocationId"),
      workLocationId: pick("workLocationId", "WorkLocationId", "locationCode", "LocationCode"),
      departmentCode: pick("departmentCode", "DepartmentCode"),
      workUnitCode: pick("workUnitCode", "WorkUnitCode", "productionUnitCode", "ProductionUnitCode"),
      productionUnitCode: pick("productionUnitCode", "ProductionUnitCode", "workUnitCode", "WorkUnitCode"),
      managerEmployeeCode: pick("managerEmployeeCode", "ManagerEmployeeCode"),
      industryType: pick("industryType", "IndustryType", "industryTypeValue", "IndustryTypeValue"),
      employmentType: pick("employmentType", "EmploymentType", "employmentTypeValue", "EmploymentTypeValue"),
      workArrangement: pick("workArrangement", "WorkArrangement", "workArrangementValue", "WorkArrangementValue"),
      ftePercent: pick("ftePercent", "FTEPercent"),
      timeZone: pick("timeZone", "TimeZone"),
      leavePolicyId: pick("leavePolicyId", "LeavePolicyId"),
      attendancePolicyId: pick("attendancePolicyId", "AttendancePolicyId"),
      shiftPatternId: pick("shiftPatternId", "ShiftPatternId", "shiftPolicyId", "ShiftPolicyId"),
      shiftPolicyId: pick("shiftPolicyId", "ShiftPolicyId", "shiftPatternId", "ShiftPatternId"),
      weeklyOffPatternId: pick("weeklyOffPatternId", "WeeklyOffPatternId"),
      holidayCalendarId: pick("holidayCalendarId", "HolidayCalendarId"),
      payGrade: pick("payGrade", "PayGrade"),
      salaryCurrency: pick("salaryCurrency", "SalaryCurrency", "salaryCurrencyValue", "SalaryCurrencyValue"),
      compensationStatus: pick("compensationStatus", "CompensationStatus", "currentCTCStatus", "CurrentCTCStatus"),

      // Current CTC summary from EmployeeMaster sync - My CTC panel remains separate and unchanged
      currentEmployeeCTCId: pick("currentEmployeeCTCId", "CurrentEmployeeCTCId"),
      currentCTCStructureId: pick("currentCTCStructureId", "CurrentCTCStructureId"),
      currentCTCEffectiveFrom: pick("currentCTCEffectiveFrom", "CurrentCTCEffectiveFrom"),
      currentCTCStatus: pick("currentCTCStatus", "CurrentCTCStatus"),
      annualCTC: pick("annualCTC", "AnnualCTC"),
      monthlyCTC: pick("monthlyCTC", "MonthlyCTC"),
      monthlyGross: pick("monthlyGross", "MonthlyGross"),
      annualEmployerCost: pick("annualEmployerCost", "AnnualEmployerCost"),
      netPayEstimateMonthly: pick("netPayEstimateMonthly", "NetPayEstimateMonthly"),
      cTCLastApprovedOn: pick("cTCLastApprovedOn", "CTCLastApprovedOn"),
      cTCRevisionNumber: pick("cTCRevisionNumber", "CTCRevisionNumber"),
      revisionDueFlag: pick("revisionDueFlag", "RevisionDueFlag") === true || String(pick("revisionDueFlag", "RevisionDueFlag")).toLowerCase() === "true" || String(pick("revisionDueFlag", "RevisionDueFlag")).toLowerCase() === "yes",

      profileCorrectionComments: pick("profileCorrectionComments", "ProfileCorrectionComments", "profileReviewComments", "ProfileReviewComments", "correctionComments", "CorrectionComments", "returnComments", "ReturnComments", "reviewComments", "ReviewComments", "comments", "Comments", "rejectionReason", "RejectionReason", "profileVerificationRemarks", "ProfileVerificationRemarks") || prev.profileCorrectionComments || prev.profileReviewComments || prev.correctionComments || "",
      profileReviewComments: pick("profileReviewComments", "ProfileReviewComments", "profileCorrectionComments", "ProfileCorrectionComments", "verificationComments", "VerificationComments", "correctionComments", "CorrectionComments", "returnComments", "ReturnComments", "reviewComments", "ReviewComments", "comments", "Comments", "rejectionReason", "RejectionReason", "profileVerificationRemarks", "ProfileVerificationRemarks") || prev.profileReviewComments || prev.profileCorrectionComments || prev.correctionComments || "",
      correctionComments: pick("correctionComments", "CorrectionComments", "profileCorrectionComments", "ProfileCorrectionComments", "returnComments", "ReturnComments", "reviewComments", "ReviewComments", "comments", "Comments", "rejectionReason", "RejectionReason", "profileVerificationRemarks", "ProfileVerificationRemarks") || prev.correctionComments || prev.profileCorrectionComments || "",
      verificationComments: pick("verificationComments", "VerificationComments", "profileReviewComments", "ProfileReviewComments", "profileCorrectionComments", "ProfileCorrectionComments", "comments", "Comments") || prev.verificationComments || "",
      profileCompletionStatus: workflowStatus.profileCompletionStatus,
      profileVerificationStatus: workflowStatus.profileVerificationStatus,
      onboardingStage: workflowStatus.onboardingStage
    }));

    const photoUrl = pick("profilePhotoLink", "ProfilePhotoLink");
    if (photoUrl) setProfileImage(getSharePointImageSrc(photoUrl));
  }, [toCountryOption, getWorkflowStatusFromEmployee, getEmployeeField]);

  const loadMyCTCForEmployee = useCallback(async (employee) => {
    const employeeCode = displayValue(getEmployeeField(employee, "employeeId", "EmployeeCode", "EmployeeId", "EmpId"));
    if (!employeeCode) {
      setMyCTC(null);
      setMyCTCHistory([]);
      setMyCTCError("Employee code is not available in Employee Master, so My CTC cannot be loaded.");
      return;
    }
    try {
      setLoadingMyCTC(true);
      setMyCTCError("");
      const [currentCTC, historyRows] = await Promise.all([
        getCurrentEmployeeCTC(employeeCode),
        getEmployeeCTCHistory(employeeCode),
      ]);
      setMyCTC(currentCTC || null);
      setMyCTCHistory(Array.isArray(historyRows) ? historyRows : []);
    } catch (error) {
      console.error("My CTC load error:", error);
      setMyCTC(null);
      setMyCTCHistory([]);
      setMyCTCError(error.message || "Unable to load your CTC details right now.");
    } finally {
      setLoadingMyCTC(false);
    }
  }, [getEmployeeField]);

  useEffect(() => {
    const loadProfileFromEmployeeMaster = async () => {
      try {
        setLoadingProfile(true);
        setProfileLoadError("");

        const currentUser = getCurrentUser(accounts);
        const loggedInEmail = currentUser?.workEmail || "";
        const loggedInEmployeeCode = currentUser?.employeeCode || "";
        if (!loggedInEmail) {
          setProfileLoadError("Please login to load your employee profile.");
          return;
        }
        const result = await getEmployeeMasterByEmail(loggedInEmail);
        console.log("Employee profile result:", result); // temporary log to check API response

        let employeeForProfile = result?.employee || result?.data || result?.profile || result?.item || result?.record || null;
        if (!employeeForProfile && Array.isArray(result?.employees)) employeeForProfile = result.employees[0];
        if (!employeeForProfile && Array.isArray(result?.value)) employeeForProfile = result.value[0];
        if (!employeeForProfile && Array.isArray(result?.body?.value)) employeeForProfile = result.body.value[0];
        if (!employeeForProfile && result && typeof result === "object" && !result.success && !result.message) employeeForProfile = result;

        // Some Get_By_Email flows return personal data but miss workflow status columns.
        // Cross-check Get All records and merge workflow status/stage from EmployeeMaster.
        try {
          const allEmployees = await getEmployeeMasters();
          const masterRow = (allEmployees || []).find((employee) => {
            const employeeEmail = getEmployeeEmail(employee);
            const employeeCode = getEmployeeCode(employee);
            const currentEmail = displayValue(loggedInEmail).toLowerCase();
            const currentCode = displayValue(loggedInEmployeeCode).toLowerCase();
            return (employeeEmail && employeeEmail === currentEmail) || (currentCode && employeeCode === currentCode);
          });
          if (masterRow) {
            employeeForProfile = mergeEmployeeMasterRows(employeeForProfile || {}, masterRow);
          }
        } catch (workflowStatusError) {
          console.warn("Workflow status merge failed. Using Get_By_Email profile only.", workflowStatusError);
        }

        applyEmployeeRecordToProfile(employeeForProfile);
      } catch (error) {
        console.error("Profile load error:", error);
        setProfileLoadError(error.message || "Unable to load employee profile.");
      } finally {
        setLoadingProfile(false);
      }
    };

    loadProfileFromEmployeeMaster();
  }, [accounts, applyEmployeeRecordToProfile, getEmployeeField, getEmployeeEmail, getEmployeeCode, mergeEmployeeMasterRows]);

  const uploadFileToFlow = async (file, fileType) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async () => {
      try {
        const base64 = reader.result.split(",")[1];
        const payload = {

          employeeId: profileData.employeeId,
          fileName: file.name,
          fileType: fileType,
          fileContent: base64
        };

        const res = await fetch("", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });

        const data = await res.json();
        if (!res.ok) {
          alert("❌ Upload failed for " + file.name);
          reject(data);
          return;
        }

        alert("✅ File uploaded successfully");
        console.log("✅ File upload response:", data);
        resolve(data);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
};

  // =====================================================
  // ✅ HANDLE CHANGE
  // =====================================================

 
  const handleChange = (e) => {
  if (!canEditProfile) return;

  const { name, value } = e.target;

  setProfileData((prev) => ({
    ...prev,
    [name]: name === "experience" ? Number(value) : value,
    ...getEditStatusPatch(prev)
  }));

  setErrors((prev) => ({ ...prev, [name]: "" }));
};

  // =====================================================
  // ✅ COUNTRY CHANGE
  // =====================================================
 
  const handleCountryChange = (selected) => {
  if (!canEditProfile) return;

  setProfileData((prev) => ({
    ...prev,
    country: selected,
    ...getEditStatusPatch(prev)
  }));

  setErrors((prev) => ({ ...prev, country: "" }));
};
  // =====================================================
  // ✅ IMAGE UPLOAD
  // =====================================================

  const handleImageUpload = async (e) => {
  if (!canEditProfile) return;

  const file = e.target.files?.[0];
  if (!file) return;

  // show local preview immediately
  const preview = URL.createObjectURL(file);
  setProfileImage(preview);

  try {
    const data = await uploadFileToFlow(file, "photo");

    const uploadedUrl =
      data.profilePhotoLink ||
      data.fileUrl ||
      data.webUrl ||
      data.url;

    if (!uploadedUrl) {
      alert("Uploaded but no URL returned from Flow");
      return;
    }

    // ✅ Save into state
    setProfileData((prev) => ({
      ...prev,
      profilePhotoLink: uploadedUrl,
      ...getEditStatusPatch(prev)
    }));

    // ✅ Convert to image URL
    setProfileImage(getSharePointImageSrc(uploadedUrl));

    } catch (err) {
      console.error(err);
      setProfileImage(null);
    }
  };

  // =====================================================
  // ✅ MASK ACCOUNT
  // =====================================================

  const maskAccountNumber = (value) => {

    if (!value) return "";

    const last4 = value.slice(-4);

    return "XXXXXXXX" + last4;
  };

  // =====================================================
  // ✅ POSTAL MOCK
  // =====================================================

  const handlePostalBlur = () => {

    if (profileData.postalCode === "500081") {

      setProfileData((prev) => ({
        ...prev,
        city: "Hyderabad",
        state: "Telangana"
      }));
    }
  };
   // =====================================================
// ✅ VALIDATION HELPERS
// =====================================================

const validateEmail = (email) => {
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const validateUrl = (url) => {
  if (!url) return true;

  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const validatePhone = (phone) => {
  if (!phone) return false;
  return phone.replace(/\D/g, "").length >= 8;
};

const validatePostalCode = (postal) => {
  if (!postal) return false;
  return /^[A-Za-z0-9\s-]{4,10}$/.test(postal);
};

const validateAccountNumber = (account) => {
  if (!account) return true;
  return /^[0-9]{6,20}$/.test(account);
};

const isAdult = (dob) => {
  if (!dob) return false;

  const birthDate = new Date(dob);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();

  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age >= 18;
};



// =====================================================
// ✅ MAIN FORM VALIDATION
// =====================================================

const validateForm = () => {
  const newErrors = {};

  // Required personal fields
  if (!profileData.firstName.trim()) {
    newErrors.firstName = "First Name is required";
  }

  if (!profileData.lastName.trim()) {
    newErrors.lastName = "Last Name is required";
  }

  if (!profileData.mobile || !validatePhone(profileData.mobile)) {
    newErrors.mobile = "Valid mobile number is required";
  }
  // ✅ DOB Mandatory + Age check
    if (!profileData.dob) {
      newErrors.dob = "Date of Birth is required";
    } else if (!isAdult(profileData.dob)) {
      newErrors.dob = "Employee must be at least 18 years old";
    }
  if (!profileData.postalCode || !validatePostalCode(profileData.postalCode)) {
    newErrors.postalCode = "Valid postal code is required";
  }

  if (!profileData.city.trim()) {
    newErrors.city = "City is required";
  }

  if (!profileData.country) {
    newErrors.country = "Country is required";
  }

  // Required qualification fields
  if (!profileData.qualification.trim()) {
    newErrors.qualification = "Highest qualification is required";
  }

  /*if (!profileData.experience.trim()) {
    newErrors.experience = "Total experience is required";
  }*/

  if (!profileData.experience || profileData.experience <= 0) {
  newErrors.experience = "Total experience is required";
  }

  // Optional email validation
  if (profileData.personalEmail && !validateEmail(profileData.personalEmail)) {
    newErrors.personalEmail = "Invalid personal email";
  }

  if (profileData.altEmail && !validateEmail(profileData.altEmail)) {
    newErrors.altEmail = "Invalid alternative email";
  }

  // Optional URL validation
  if (profileData.linkedin && !validateUrl(profileData.linkedin)) {
    newErrors.linkedin = "Invalid LinkedIn URL";
  }

  if (profileData.github && !validateUrl(profileData.github)) {
    newErrors.github = "Invalid GitHub URL";
  }

  if (profileData.portfolio && !validateUrl(profileData.portfolio)) {
    newErrors.portfolio = "Invalid portfolio URL";
  }

  // Optional account validation
  if (
    profileData.accountNumber &&
    !validateAccountNumber(profileData.accountNumber)
  ) {
    newErrors.accountNumber = "Account number must be 6 to 20 digits";
  }

  setErrors(newErrors);

  return Object.keys(newErrors).length === 0;
};

  // =====================================================
  // ✅ SAVE
  // =====================================================
 
 
const handleSave = async () => {
  if (isProfileLocked) {
    alert("Profile is locked because it is pending verification or already verified.");
    return;
  }

  console.log("✅ Save button clicked");
  const isValid = validateForm();

  if (!isValid) {
    alert("Please fix validation errors before saving.");
    return;
  }

  const derivedCompletionStatus = getAutoCompletionStatus(profileData, completionPercentage);
  const saveVerificationStatus = isVerificationRejected
    ? PROFILE_VERIFICATION_STATUS.NOT_VERIFIED
    : currentVerificationStatus;
  const saveOnboardingStage = isVerificationRejected
    ? ONBOARDING_STAGE.PROFILE_PENDING
    : (profileData.onboardingStage || ONBOARDING_STAGE.PROFILE_PENDING);

  try {
    const payload = {

      employeeId: profileData.employeeId,
      workEmail: profileData.workEmail,
      department: profileData.department,
      designation: profileData.designation,
      gradeLevel: profileData.gradeLevel,
      reportingManager: profileData.reportingManager,
      location: profileData.location,
      joiningDate: profileData.joiningDate || null,
      employmentStatus: profileData.employmentStatus,
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      fullName: profileData.fullName,
      mobile: profileData.mobile,
      personalEmail: profileData.personalEmail,
      altEmail: profileData.altEmail,
      dob: profileData.dob,
      gender: profileData.gender,
      nationality: profileData.nationality,
      address1: profileData.address1,
      address2: profileData.address2,
      postalCode: profileData.postalCode,
      city: profileData.city,
      state: profileData.state,
      country: profileData.country?.label || "",
      bloodGroup: profileData.bloodGroup,
      emergencyContactName: profileData.emergencyContactName,
      emergencyContactPhone: profileData.emergencyContactPhone,
      relationship: profileData.relationship,
      familyDoctorDetails: profileData.familyDoctorDetails,
      medicalHistory: profileData.medicalHistory,
      qualification: profileData.qualification,
      institute: profileData.institute,
      graduationYear: parseInt(profileData.graduationYear) || null,
      experience: parseInt(profileData.experience) || 0,
      domain: profileData.domain,
      skills: profileData.skills,
      lastDesignation: profileData.lastDesignation,
      lastCompany: profileData.lastCompany,
      lastRelievingDate: profileData.lastRelievingDate || null,
      qualificationSummary: profileData.qualificationSummary,
      experienceSummary: profileData.experienceSummary,
      achievements: profileData.achievements,
      certifications: profileData.certifications,
      trainingsCompleted: profileData.trainingsCompleted,
      awards: profileData.awards,
      performanceSummary: profileData.performanceSummary,
      linkedin: profileData.linkedin,
      github: profileData.github,
      portfolio: profileData.portfolio,
      bankName: profileData.bankName,
      accountHolder: profileData.accountHolder,
      accountNumber: profileData.accountNumber,
      ifsc: profileData.ifsc,
      iban: profileData.iban,
      swift: profileData.swift,
      community: profileData.community,
      visibility: profileData.visibility,
      profilePhotoLink: profileData.profilePhotoLink,
      resumeLink: profileData.resumeLink,
      attachmentsLink: profileData.attachmentsLink,
      profileCompletionStatus: derivedCompletionStatus,
      profileVerificationStatus: saveVerificationStatus,
      profileCorrectionComments: profileData.profileCorrectionComments || profileData.correctionComments || "",
      onboardingStage: saveOnboardingStage
    };

    console.log("Sending payload:", payload);
    const flowUrl = "";

    const response = await fetch(flowUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    console.log("Response:", data);

    if (response.ok) {
      setProfileData((prev) => ({
        ...prev,
        profileCompletionStatus: derivedCompletionStatus,
        profileVerificationStatus: saveVerificationStatus,
        onboardingStage: saveOnboardingStage
      }));
      setEditMode(false);
      alert("✅ Profile saved successfully!");
    } else {
      alert("❌ Failed to save profile");
    }
  } catch (error) {
    console.error("❌ ERROR:", error);
    alert("❌ Error while saving profile");
  }
};

// =====================================================
// ✅ PHASE 3A SUBMIT PROFILE
// =====================================================
const handleSubmitProfile = async () => {
  if (isProfileLocked) {
    alert("Profile is already submitted for verification or verified.");
    return;
  }

  if (completionPercentage < 100) {
    alert("Please complete all required profile sections before submitting.");
    return;
  }

  const isValid = validateForm();
  if (!isValid) {
    alert("Please fix validation errors before submitting.");
    return;
  }

  if (!SUBMIT_PROFILE_FLOW_URL || SUBMIT_PROFILE_FLOW_URL === "PASTE_SUBMIT_PROFILE_FLOW_URL_HERE") {
    alert("Submit Profile flow URL is not configured yet. Please paste your Submit_Profile Power Automate URL in Profile.js.");
    return;
  }

  try {
    const response = await fetch(SUBMIT_PROFILE_FLOW_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({

        employeeId: profileData.employeeId,
        workEmail: profileData.workEmail,
        profileCompletionStatus: PROFILE_COMPLETION_STATUS.SUBMITTED,
        profileVerificationStatus: PROFILE_VERIFICATION_STATUS.PENDING_VERIFICATION,
        onboardingStage: ONBOARDING_STAGE.PROFILE_PENDING
      })
    });

    let result = {};
    try {
      result = await response.json();
    } catch {
      result = {};
    }

    if (response.ok && result.success !== false) {
      setProfileData((prev) => ({
        ...prev,
        profileCompletionStatus: PROFILE_COMPLETION_STATUS.SUBMITTED,
        profileVerificationStatus: PROFILE_VERIFICATION_STATUS.PENDING_VERIFICATION,
        onboardingStage: ONBOARDING_STAGE.PROFILE_PENDING
      }));
      setEditMode(false);
      alert("✅ Profile submitted successfully for verification.");
    } else {
      console.error("Submit response:", result);
      alert(result.message || "❌ Failed to submit profile.");
    }
  } catch (error) {
    console.error("Submit profile error:", error);
    alert("❌ Error while submitting profile.");
  }
};

  // =====================================================
  // ✅ REFRESH
  // =====================================================

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleOpenMyCTC = async () => {
    setShowMyCTCPanel(true);
    setShowMyCTCHistory(false);
    await loadMyCTCForEmployee({
      employeeId: profileData.employeeId,
      workEmail: profileData.workEmail,
    });
  };

  const handleCloseMyCTC = () => {
    setShowMyCTCPanel(false);
    setShowMyCTCHistory(false);
  };

  // =====================================================
// ✅ RESUME EXTRACTION - LOCAL PYTHON API
// =====================================================

const handleResumeFileChange = async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  const allowedTypes = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ];

  const isPdf = file.name.toLowerCase().endsWith(".pdf");
  const isDocx = file.name.toLowerCase().endsWith(".docx");

  if (!allowedTypes.includes(file.type) && !isPdf && !isDocx) {
    alert("Only PDF and DOCX resume files are allowed.");
    return;
  }

  if (!canEditProfile) return;
  setResumeFile(file);

  try {
    const data = await uploadFileToFlow(file, "resume");
    const uploadedUrl = data.resumeLink || data.fileUrl || data.webUrl || data.url;

    if (uploadedUrl) {
      setProfileData((prev) => ({
        ...prev,
        resumeLink: uploadedUrl,
        ...getEditStatusPatch(prev)
      }));
    }
  } catch (error) {
    console.error("Resume upload error:", error);
  }
};
const handleExtractResume = async () => {
  if (!resumeFile) {
    alert("Please choose a resume file first.");
    return;
  }

  try {
    setExtractingResume(true);

    const formData = new FormData();
    formData.append("file", resumeFile);

    const response = await fetch("/tracka-local/v1/resume/extract", {
      method: "POST",
      body: formData
    });

    const result = await response.json();

    if (!result.success) {
      alert(result.message || "Resume extraction failed.");
      return;
    }

    setExtractedResumeData(result.extracted || {});
    setShowResumeModal(false);
    setShowExtractReviewModal(true);

  } catch (error) {
    console.error("Resume extraction error:", error);
    alert("Resume extraction service is unavailable. Please try again later.");
  } finally {
    setExtractingResume(false);
  }
};

const applyExtractedResumeData = () => {
  if (!extractedResumeData) return;

  setProfileData((prev) => ({
    ...prev,

    firstName: extractedResumeData.firstName || prev.firstName,
    lastName: extractedResumeData.lastName || prev.lastName,
    fullName: extractedResumeData.fullName || prev.fullName,

    mobile: extractedResumeData.mobile || prev.mobile,
    personalEmail: extractedResumeData.personalEmail || prev.personalEmail,

    qualification: extractedResumeData.qualification || prev.qualification,
    institute: extractedResumeData.institute || prev.institute,
    graduationYear: extractedResumeData.graduationYear || prev.graduationYear,

    experience: extractedResumeData.experience || prev.experience,
    skills: extractedResumeData.skills || prev.skills,

    qualificationSummary:
      extractedResumeData.qualificationSummary || prev.qualificationSummary,

    experienceSummary:
      extractedResumeData.experienceSummary || prev.experienceSummary,

    achievements: extractedResumeData.achievements || prev.achievements,
    certifications: extractedResumeData.certifications || prev.certifications,

    community: extractedResumeData.community || prev.community,

    linkedin: extractedResumeData.linkedin || prev.linkedin,
    github: extractedResumeData.github || prev.github,
    portfolio: extractedResumeData.portfolio || prev.portfolio
  }));

  setEditMode(true);
  setShowExtractReviewModal(false);
  setExtractedResumeData(null);

  alert("Extracted resume data applied. Please review and correct before saving.");
};

const cancelExtractedResumeData = () => {
  setShowExtractReviewModal(false);
  setExtractedResumeData(null);
};

const handleAttachmentUpload = async (e) => {
  const files = e.target.files;
  if (!canEditProfile) return;

  const uploadedLinks = [];

  for (let i = 0; i < files.length; i++) {
    try {
      const data = await uploadFileToFlow(files[i], "attachment");
      const uploadedUrl = data.attachmentsLink || data.fileUrl || data.webUrl || data.url;
      if (uploadedUrl) uploadedLinks.push(uploadedUrl);
    } catch (error) {
      console.error("Attachment upload error:", error);
    }
  }

  if (uploadedLinks.length > 0) {
    setProfileData((prev) => ({
      ...prev,
      attachmentsLink: [prev.attachmentsLink, ...uploadedLinks].filter(Boolean).join("; "),
      ...getEditStatusPatch(prev)
    }));
  }
};
  // =====================================================
// ✅ FIELD RENDER HELPERS
// =====================================================

const renderError = (fieldName) => {
  if (!errors[fieldName]) return null;

  return (
    <span className="error-text">
      {errors[fieldName]}
    </span>
  );
};

const inputClass = (fieldName) => {
  return errors[fieldName] ? "error-input" : "";
};

const renderInput = ({
  label,
  name,
  type = "text",
  required = false,
  disabled = false,
  readOnly = false,
  onBlur
}) => {
  const fieldDisabled = disabled || !canEditProfile;
  return (
    <div className="field-group">
      <label>{label} {required && <span className="required-star">*</span>}</label>
      <input type={type} name={name} value={profileData[name] || ""} onChange={handleChange} onBlur={onBlur} disabled={fieldDisabled} readOnly={readOnly} className={inputClass(name)} />
      {renderError(name)}
    </div>
  );
};

const renderTextarea = ({ label, name, required = false, disabled = false }) => {
  const fieldDisabled = disabled || !canEditProfile;
  return (
    <div className="field-group full-width">
      <label>{label} {required && <span className="required-star">*</span>}</label>
      <textarea name={name} value={profileData[name] || ""} onChange={handleChange} disabled={fieldDisabled} className={inputClass(name)} />
      {renderError(name)}
    </div>
  );
};

const renderSelect = ({ label, name, options, required = false, disabled = false }) => {
  const fieldDisabled = disabled || !canEditProfile;
  return (
    <div className="field-group">
      <label>{label} {required && <span className="required-star">*</span>}</label>
      <select name={name} value={profileData[name] || ""} onChange={handleChange} disabled={fieldDisabled} className={inputClass(name)}>
        <option value="">Select</option>
        {options.map((option) => (<option key={option} value={option}>{option}</option>))}
      </select>
      {renderError(name)}
    </div>
  );
};

const renderPhone = ({ label, name, required = false, defaultCountry = "in" }) => {
  return (
    <div className={`field-group ${errors[name] ? "phone-error" : ""}`}>
      <label>{label} {required && <span className="required-star">*</span>}</label>
      <PhoneInput
        country={defaultCountry}
        value={profileData[name] || ""}
        onChange={(phone) => {
          if (!canEditProfile) return;
          setProfileData((prev) => ({
            ...prev,
            [name]: phone,
            ...getEditStatusPatch(prev)
          }));
          setErrors((prev) => ({ ...prev, [name]: "" }));
        }}
        disabled={!canEditProfile}
        inputClass="phone-input"
        containerClass="phone-container"
        enableSearch
      />
      {renderError(name)}
    </div>
  );
};

const renderCountryPicker = () => {
  return (
    <div className="field-group">
      <label>Country <span className="required-star">*</span></label>
      <Select options={countries} value={profileData.country} onChange={handleCountryChange} isDisabled={!canEditProfile} placeholder="Select..." className={`country-select-wrapper ${errors.country ? "react-select-error" : ""}`} classNamePrefix="country-select" />
      {renderError("country")}
    </div>
  );
};

const renderAccountNumber = () => {
  return (
    <div className="field-group">
      <label>Account Number</label>
      <input name="accountNumber" value={canEditProfile ? profileData.accountNumber || "" : maskAccountNumber(profileData.accountNumber)} onChange={handleChange} disabled={!canEditProfile} className={errors.accountNumber ? "error-input" : ""} placeholder="Enter account number" />
      {renderError("accountNumber")}
    </div>
  );
};

const renderReadOnlyField = (label, value) => (
  <div className="field-group">
    <label>{label}</label>
    <input value={displayValue(value) || "-"} disabled />
  </div>
);

const renderReadOnlyMini = (label, value) => (
  <div className="readonly-mini-field">
    <span>{label}</span>
    <strong>{displayValue(value) || "-"}</strong>
  </div>
);

const renderEmploymentOrganizationSection = () => (
  <div className="profile-card profile-master-card">
    <div className="card-header between">
      <div>
        <h2>EmployeeMaster Details</h2>
        <p className="profile-card-subtitle">
          Read-only HR master data from EmployeeMaster. Corrections to these fields are handled by HR/Admin. Personal profile details below can be completed by the employee during profile completion and verification.
        </p>
      </div>
    </div>

    <div className="master-section-block">
      <h3>Organization</h3>
      {renderReadOnlyMini("Country", profileData.country?.label || profileData.country)}
      {renderReadOnlyMini("Country Code", profileData.countryCode)}
      {renderReadOnlyMini("Legal Entity", profileData.legalEntity)}
      {renderReadOnlyMini("Legal Entity Code", profileData.legalEntityCode)}
      {renderReadOnlyMini("Industry Type", profileData.industryType)}
      {renderReadOnlyMini("Region / State", profileData.regionState)}
      {renderReadOnlyMini("Location", profileData.location)}
      {renderReadOnlyMini("Location Code", profileData.locationCode || profileData.workLocationId)}
    </div>

    <div className="master-section-block">
      <h3>Work Structure</h3>
      {renderReadOnlyMini("Department", profileData.department)}
      {renderReadOnlyMini("Department Code", profileData.departmentCode)}
      {renderReadOnlyMini("Work Unit Code", profileData.workUnitCode || profileData.productionUnitCode)}
      {renderReadOnlyMini("Designation", profileData.designation)}
      {renderReadOnlyMini("Level / Grade", profileData.gradeLevel)}
      {renderReadOnlyMini("Reporting Manager", profileData.reportingManager)}
      {renderReadOnlyMini("Manager Employee Code", profileData.managerEmployeeCode)}
    </div>

    <div className="master-section-block">
      <h3>Employment</h3>
      {renderReadOnlyMini("Joining Date", profileData.joiningDate)}
      {renderReadOnlyMini("Employment Status", profileData.employmentStatus)}
      {renderReadOnlyMini("Employment Type", profileData.employmentType)}
      {renderReadOnlyMini("Work Arrangement", profileData.workArrangement)}
      {renderReadOnlyMini("FTE %", profileData.ftePercent)}
      {renderReadOnlyMini("Time Zone", profileData.timeZone)}
      {renderReadOnlyMini("Pay Grade", profileData.payGrade)}
      {renderReadOnlyMini("Salary Currency", profileData.salaryCurrency)}
    </div>

    <div className="master-section-block">
      <h3>Policies</h3>
      {renderReadOnlyMini("Leave Policy ID", profileData.leavePolicyId)}
      {renderReadOnlyMini("Attendance Policy ID", profileData.attendancePolicyId)}
      {renderReadOnlyMini("Shift Pattern ID", profileData.shiftPatternId || profileData.shiftPolicyId)}
      {renderReadOnlyMini("Weekly Off Pattern ID", profileData.weeklyOffPatternId)}
      {renderReadOnlyMini("Holiday Calendar ID", profileData.holidayCalendarId)}
    </div>
  </div>
);

const renderMyCTCSection = () => {
  const currency = normalizeCTCText(getCTCField(myCTC || {}, "salaryCurrency", "SalaryCurrency", "field_18")) || profileData.salaryCurrency || "INR";
  const componentRows = parseComponentBreakupJson(getCTCField(myCTC || {}, "componentBreakupJson", "ComponentBreakupJson", "field_31"));
  const visibleHistory = (myCTCHistory || []).slice(0, 8);
  const directMonthlyNetPay = toCTCNumber(getCTCField(myCTC || {}, "netPayEstimateMonthly", "NetPayEstimateMonthly", "field_30"));
  const monthlyGross = toCTCNumber(getCTCField(myCTC || {}, "monthlyGross", "MonthlyGross", "field_22"));
  const monthlyDeductions = toCTCNumber(getCTCField(myCTC || {}, "monthlyEmployeeDeductions", "MonthlyEmployeeDeductions", "field_26"));
  const monthlyNetPay = directMonthlyNetPay !== null
    ? directMonthlyNetPay
    : monthlyGross !== null && monthlyDeductions !== null
      ? Math.max(monthlyGross - monthlyDeductions, 0)
      : null;
  const summaryItems = [
    { label: "Annual CTC", value: formatCTCMoney(getCTCField(myCTC || {}, "annualCTC", "AnnualCTC", "field_19"), currency) },
    { label: "Monthly CTC", value: formatCTCMoney(getCTCField(myCTC || {}, "monthlyCTC", "MonthlyCTC", "field_20"), currency) },
    { label: "Monthly Gross", value: formatCTCMoney(getCTCField(myCTC || {}, "monthlyGross", "MonthlyGross", "field_22"), currency) },
    { label: "Monthly Net Pay", value: formatCTCMoney(monthlyNetPay, currency) },
    { label: "Employer Cost", value: formatCTCMoney(getCTCField(myCTC || {}, "annualEmployerCost", "AnnualEmployerCost", "field_23"), currency) },
    { label: "Effective From", value: formatCTCDate(getCTCField(myCTC || {}, "effectiveFrom", "EffectiveFrom", "field_32")) },
    { label: "Revision", value: normalizeCTCText(getCTCField(myCTC || {}, "revisionNumber", "RevisionNumber", "field_45")) || "-" },
  ];
  return (
    <div className="profile-card" style={{ marginBottom: "18px" }}>
      <div className="card-header between">
        <div>
          <h2>My CTC</h2>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "14px" }}>Read-only compensation details for your own employee record only. This panel opens only when you click My CTC.</p>
        </div>
      </div>
      {loadingMyCTC && <div style={{ border: "1px solid #bfdbfe", background: "#eef6ff", color: "#0f6cbd", borderRadius: "10px", padding: "12px", fontWeight: 700 }}>Loading your current CTC details...</div>}
      {!loadingMyCTC && myCTCError && <div style={{ border: "1px solid #fecaca", background: "#fef2f2", color: "#b91c1c", borderRadius: "10px", padding: "12px", fontWeight: 700 }}>{myCTCError}</div>}
      {!loadingMyCTC && !myCTCError && !myCTC && <div style={{ border: "1px solid #e5edf6", background: "#f6f8fb", color: "#64748b", borderRadius: "10px", padding: "12px", fontWeight: 700 }}>No approved current CTC is available for your employee code yet.</div>}
      {!loadingMyCTC && !myCTCError && myCTC && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "16px" }}>
            {summaryItems.map((item) => <div key={item.label} style={{ border: "1px solid #e5edf6", borderRadius: "12px", padding: "12px", background: "#ffffff" }}><div style={{ color: "#64748b", fontSize: "12px", fontWeight: 700 }}>{item.label}</div><div style={{ color: "#172033", fontSize: "18px", fontWeight: 800, marginTop: "4px" }}>{item.value}</div></div>)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px", marginBottom: "16px" }}>
            <div><strong>Employee CTC ID:</strong> {normalizeCTCText(getCTCField(myCTC, "employeeCTCId", "EmployeeCTCId", "field_1")) || "-"}</div>
            <div><strong>CTC Structure:</strong> {normalizeCTCText(getCTCField(myCTC, "ctcStructureId", "CTCStructureId", "field_16")) || "-"}</div>
            <div><strong>Status:</strong> {normalizeCTCText(getCTCField(myCTC, "ctcStatus", "CTCStatus", "field_34")) || "Approved"}</div>
            <div><strong>Currency:</strong> {currency}</div>
          </div>
          <div style={{ overflowX: "auto", border: "1px solid #e5edf6", borderRadius: "12px", marginBottom: "14px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "760px", fontSize: "13px" }}>
              <thead><tr><th style={{ textAlign: "left", padding: "10px", background: "#f6f8fb", borderBottom: "1px solid #e5e7eb" }}>Code</th><th style={{ textAlign: "left", padding: "10px", background: "#f6f8fb", borderBottom: "1px solid #e5e7eb" }}>Component</th><th style={{ textAlign: "left", padding: "10px", background: "#f6f8fb", borderBottom: "1px solid #e5e7eb" }}>Type</th><th style={{ textAlign: "right", padding: "10px", background: "#f6f8fb", borderBottom: "1px solid #e5e7eb" }}>Annual</th><th style={{ textAlign: "right", padding: "10px", background: "#f6f8fb", borderBottom: "1px solid #e5e7eb" }}>Monthly</th></tr></thead>
              <tbody>
                {componentRows.length === 0 && <tr><td colSpan="5" style={{ padding: "14px", textAlign: "center", color: "#64748b" }}>Component breakup is not available.</td></tr>}
                {componentRows.map((row, index) => <tr key={`${normalizeCTCText(row.componentCode || row.ComponentCode) || "COMP"}-${index}`}><td style={{ padding: "10px", borderBottom: "1px solid #e5e7eb" }}>{normalizeCTCText(row.componentCode || row.ComponentCode) || "-"}</td><td style={{ padding: "10px", borderBottom: "1px solid #e5e7eb" }}>{normalizeCTCText(row.componentName || row.ComponentName) || "-"}</td><td style={{ padding: "10px", borderBottom: "1px solid #e5e7eb" }}>{normalizeCTCText(row.applicabilityType || row.ApplicabilityType || row.componentCategory || row.ComponentCategory) || "-"}</td><td style={{ padding: "10px", borderBottom: "1px solid #e5e7eb", textAlign: "right" }}>{formatCTCMoney(row.annualAmount ?? row.AnnualAmount, currency)}</td><td style={{ padding: "10px", borderBottom: "1px solid #e5e7eb", textAlign: "right" }}>{formatCTCMoney(row.monthlyAmount ?? row.MonthlyAmount, currency)}</td></tr>)}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <small style={{ color: "#64748b" }}>Showing approved/current CTC only. Drafts, approval actions, and HR setup remain restricted to HR/Finance roles.</small>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button type="button" className="secondary-btn" onClick={() => setShowMyCTCHistory((prev) => !prev)} disabled={!myCTCHistory.length}>{showMyCTCHistory ? "Hide History" : `Show History (${myCTCHistory.length})`}</button>
              <button type="button" className="ghost-btn" onClick={handleCloseMyCTC}>Close</button>
            </div>
          </div>
          {showMyCTCHistory && <div style={{ marginTop: "14px", overflowX: "auto", border: "1px solid #e5edf6", borderRadius: "12px" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: "760px", fontSize: "13px" }}><thead><tr><th style={{ textAlign: "left", padding: "10px", background: "#f6f8fb", borderBottom: "1px solid #e5e7eb" }}>Effective From</th><th style={{ textAlign: "left", padding: "10px", background: "#f6f8fb", borderBottom: "1px solid #e5e7eb" }}>Change Type</th><th style={{ textAlign: "left", padding: "10px", background: "#f6f8fb", borderBottom: "1px solid #e5e7eb" }}>Revision</th><th style={{ textAlign: "right", padding: "10px", background: "#f6f8fb", borderBottom: "1px solid #e5e7eb" }}>Annual CTC</th><th style={{ textAlign: "right", padding: "10px", background: "#f6f8fb", borderBottom: "1px solid #e5e7eb" }}>Monthly Gross</th><th style={{ textAlign: "left", padding: "10px", background: "#f6f8fb", borderBottom: "1px solid #e5e7eb" }}>Approved On</th></tr></thead><tbody>{visibleHistory.map((row, index) => { const rowCurrency = normalizeCTCText(row.salaryCurrency || row.SalaryCurrency) || currency; return <tr key={`${normalizeCTCText(row.ctcHistoryId || row.CTCHistoryId || row.employeeCTCId || row.EmployeeCTCId) || "HIST"}-${index}`}><td style={{ padding: "10px", borderBottom: "1px solid #e5e7eb" }}>{formatCTCDate(row.effectiveFrom || row.EffectiveFrom)}</td><td style={{ padding: "10px", borderBottom: "1px solid #e5e7eb" }}>{normalizeCTCText(row.changeType || row.ChangeType) || "-"}</td><td style={{ padding: "10px", borderBottom: "1px solid #e5e7eb" }}>{normalizeCTCText(row.revisionNumber || row.RevisionNumber) || "-"}</td><td style={{ padding: "10px", borderBottom: "1px solid #e5e7eb", textAlign: "right" }}>{formatCTCMoney(row.newAnnualCTC ?? row.NewAnnualCTC, rowCurrency)}</td><td style={{ padding: "10px", borderBottom: "1px solid #e5e7eb", textAlign: "right" }}>{formatCTCMoney(row.newMonthlyGross ?? row.NewMonthlyGross, rowCurrency)}</td><td style={{ padding: "10px", borderBottom: "1px solid #e5e7eb" }}>{formatCTCDate(row.approvedOn || row.ApprovedOn || row.changedOn || row.ChangedOn)}</td></tr>; })}</tbody></table></div>}
        </>
      )}
      {(loadingMyCTC || myCTCError || !myCTC) && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "14px" }}>
          <button type="button" className="ghost-btn" onClick={handleCloseMyCTC}>Close</button>
        </div>
      )}
    </div>
  );
};
    if (loadingProfile) {
    return (
      <div className="profile-page">
        <div className="profile-card">
          <h2>Loading employee profile...</h2>
          <p>Please wait while your HR master data is loaded.</p>
        </div>
      </div>
    );
  }

  if (profileLoadError) {
    return (
      <div className="profile-page">
        <div className="profile-card">
          <h2>Profile Not Available</h2>
          <p>{profileLoadError}</p>
          <button className="secondary-btn" onClick={handleRefresh}>Refresh</button>
        </div>
      </div>
    );
  }

  return (

    <div className="profile-page">
      <style>{`
        /* Profile final redesign - 25 Jun 2026 */
        .profile-page { background: #f6f8fb; min-height: 100vh; padding-bottom: 24px !important; }
        .profile-top-hero,
        .profile-page > div[style*="background: #ffffff"],
        .profile-layout,
        .action-bar { max-width: 1180px !important; margin-left: auto !important; margin-right: auto !important; }
        .profile-top-hero { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 18px; align-items: center; margin-bottom: 14px !important; padding: 6px 0 4px; }
        .profile-top-title { margin: 0; font-size: clamp(24px, 3vw, 34px); line-height: 1.16; color: #172033; font-family: "Segoe UI", Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif; font-weight: 750; letter-spacing: -0.02em; }
        .profile-top-meta { display: flex; flex-wrap: wrap; gap: 8px 12px; align-items: center; margin-top: 8px; color: #475569; font-size: 15px; font-family: "Segoe UI", Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif; font-weight: 650; }
        .profile-top-code { color: #0078d4; font-weight: 900; }
        .profile-top-email { overflow-wrap: anywhere; }
        .profile-top-photo-wrap { display: grid; justify-items: center; gap: 8px; }
        .profile-top-photo { width: 86px; height: 86px; border-radius: 22px; overflow: hidden; background: #eef2f7; display: grid; place-items: center; border: 1px solid #dbeafe; box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08); }
        .profile-top-photo img { width: 100%; height: 100%; object-fit: cover; }
        .profile-top-photo span { font-size: 11px; color: #64748b; text-align: center; padding: 6px; }
        .profile-top-photo-button { border: 1px solid #bfdbfe; background: #eef6ff; color: #0078d4; border-radius: 999px; padding: 7px 12px; font-size: 12px; font-weight: 800; cursor: pointer; }
        .profile-top-photo-button:disabled { opacity: 0.55; cursor: not-allowed; }
        .profile-header, .profile-sidebar, .profile-info-card, .profile-image-card { display: none !important; }
        .profile-layout { display: flex !important; flex-direction: column !important; gap: 16px !important; align-items: stretch !important; margin-top: 16px !important; }
        .profile-master-column, .profile-content { position: relative !important; top: auto !important; max-height: none !important; overflow: visible !important; padding-right: 0 !important; width: 100% !important; min-width: 0 !important; grid-column: auto !important; grid-row: auto !important; }
        .profile-master-column { order: 1 !important; }
        .profile-content { order: 2 !important; }
        .profile-card, .profile-master-card { box-sizing: border-box !important; }
        .profile-master-card { margin-bottom: 0 !important; display: grid !important; grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 14px !important; }
        .profile-master-card > .card-header { grid-column: 1 / -1 !important; }
        .profile-card-subtitle { margin: 4px 0 0 !important; color: #64748b !important; font-size: 13px !important; line-height: 1.45 !important; }
        .master-section-block { border: 1px solid #e5e7eb !important; background: #ffffff !important; border-radius: 14px !important; padding: 14px !important; margin: 0 !important; }
        .master-section-block h3 { margin: 0 0 10px !important; font-size: 16px !important; color: #172033 !important; padding-bottom: 8px !important; border-bottom: 1px solid #e5e7eb !important; }
        .readonly-mini-field { display: grid !important; grid-template-columns: minmax(145px, 0.75fr) minmax(180px, 1.25fr) !important; gap: 10px !important; align-items: start !important; padding: 8px 0 !important; border-bottom: 1px dashed #e5e7eb !important; font-size: 13px !important; }
        .readonly-mini-field:last-child { border-bottom: none !important; }
        .readonly-mini-field span { color: #64748b !important; font-weight: 800 !important; }
        .readonly-mini-field strong { color: #172033 !important; font-weight: 800 !important; overflow-wrap: anywhere !important; }
        .profile-content > .profile-card { margin-bottom: 16px !important; }
        .profile-content .form-grid { grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)) !important; gap: 14px !important; }
        .profile-content textarea { min-height: 110px !important; resize: vertical !important; white-space: pre-wrap !important; overflow-wrap: anywhere !important; }
        .action-bar { position: static !important; left: auto !important; right: auto !important; bottom: auto !important; margin-top: 18px !important; border-radius: 16px !important; box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08) !important; z-index: auto !important; }
        @media (max-width: 900px) { .profile-master-card { grid-template-columns: 1fr !important; } .profile-top-hero { grid-template-columns: 1fr !important; justify-items: start; } .profile-top-photo-wrap { justify-items: start; grid-template-columns: auto auto; align-items: center; } }
        @media (max-width: 720px) { .profile-page { padding: 12px !important; } .profile-top-hero, .profile-page > div[style*="background: #ffffff"], .profile-layout, .action-bar { max-width: 100% !important; } .profile-top-title { margin: 0; font-size: clamp(24px, 3vw, 34px); line-height: 1.16; color: #172033; font-family: "Segoe UI", Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif; font-weight: 750; letter-spacing: -0.02em; } .profile-top-meta { display: flex; flex-wrap: wrap; gap: 8px 12px; align-items: center; margin-top: 8px; color: #475569; font-size: 15px; font-family: "Segoe UI", Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif; font-weight: 650; } .profile-top-photo { width: 72px; height: 72px; border-radius: 18px; } .readonly-mini-field { grid-template-columns: 1fr !important; gap: 3px !important; } .action-bar, .left-actions, .right-actions { display: grid !important; grid-template-columns: 1fr !important; width: 100% !important; gap: 10px !important; } .action-bar button { width: 100% !important; } }
      `}</style>
{/* ================================================= */}
      {/* ================================================= */}
      {/* ✅ PAGE HEADER - identity + photo */}
      {/* ================================================= */}
      <div className="profile-top-hero fm-hr-hero">
        <div>
          <h1 className="profile-top-title">
            {(displayValue(profileData.firstName) || displayValue(profileData.fullName) || "Employee")} Profile
          </h1>
          <div className="profile-top-meta">
            <span className="profile-top-code">{displayValue(profileData.employeeId) || "-"}</span>
            {displayValue(profileData.designation) && <span>• {displayValue(profileData.designation)}</span>}
            {displayValue(profileData.workEmail) && <span className="profile-top-email">• {displayValue(profileData.workEmail)}</span>}
          </div>
        </div>
        <div className="profile-top-photo-wrap">
          <div className="profile-top-photo">
            {profileImage ? (
              <img
                src={profileImage}
                alt="profile"
                onError={() => {
                  console.warn("Profile image failed to load:", profileImage);
                  setProfileImage(null);
                }}
              />
            ) : (
              <span>Profile Photo</span>
            )}
          </div>
          <input type="file" hidden ref={imageInputRef} accept="image/*" onChange={handleImageUpload} />
          <button type="button" className="profile-top-photo-button" onClick={() => imageInputRef.current?.click()} disabled={!canEditProfile}>
            Change Photo
          </button>
        </div>
      </div>

      {/* ✅ PHASE 3A STATUS + COMPLETION BAR */}
      {/* ================================================= */}
      <div style={{ background: "#ffffff", border: "1px solid #e5edf6", borderRadius: "14px", padding: "16px", marginBottom: "18px", boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            <span style={getStatusBadgeStyle(completionPercentage === 100 ? "success" : "info")}>Completion: {effectiveProfileCompletionStatus}</span>
            <span style={getStatusBadgeStyle(verificationBadgeType)}>Verification: {currentVerificationStatus}</span>
            <span style={getStatusBadgeStyle("info")}>Stage: {displayOnboardingStage}</span>
          </div>
          {profileStatusMessage && (
            <strong style={{ color: profileStatusMessageType === "success" ? "#047857" : profileStatusMessageType === "danger" ? "#b91c1c" : "#92400e", fontSize: "13px" }}>
              {profileStatusMessage}
            </strong>
          )}
        </div>
        <div style={{ height: "12px", background: "#e5e7eb", borderRadius: "999px", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${completionPercentage}%`, background: completionPercentage === 100 ? "#16a34a" : "#0ea5e9", transition: "width 0.25s ease" }} />
        </div>
        <div style={{ marginTop: "8px", color: "#475569", fontSize: "13px", fontWeight: 600 }}>Profile completion: {completionPercentage}%</div>
        {isVerificationRejected && returnCorrectionComments && (
          <div style={{ marginTop: "12px", border: "1px solid #fecaca", background: "#fff7f7", color: "#b91c1c", borderRadius: "12px", padding: "12px 14px", fontSize: "14px", lineHeight: 1.45 }}>
            <strong>HR return comments:</strong> {returnCorrectionComments}
          </div>
        )}
      </div>

      {/* ================================================= */}
      {/* ✅ MAIN LAYOUT */}
      {/* ================================================= */}

      <div className="profile-layout">
{/* ================================================= */}
        {/* ✅ RIGHT CONTENT */}
        {/* ================================================= */}

        <div className="profile-master-column">
          {renderEmploymentOrganizationSection()}
        </div>

        <div className="profile-content">
{/* ================================================= */}
          {/* ✅ PERSONAL DETAILS */}
          {/* ================================================= */}

          <div className="profile-card">
            <div className="card-header">
              <h2>Personal Details</h2>
            </div>

            <div className="form-grid">
              {renderInput({ label: "First Name", name: "firstName", required: true })}
              {renderInput({ label: "Last Name", name: "lastName", required: true })}
              {renderInput({ label: "Full Name", name: "fullName" })}

              {renderPhone({ label: "Mobile Number", name: "mobile", required: true })}

              {renderInput({ label: "Personal Email", name: "personalEmail", type: "email" })}
              {renderInput({ label: "Alternative Email", name: "altEmail", type: "email" })}

              {renderInput({ label: "Date of Birth", name: "dob", type: "date", required: true })}

              {renderSelect({
                label: "Gender",
                name: "gender",
                options: ["Male", "Female", "Prefer not to say"]
              })}

              {renderInput({ label: "Nationality", name: "nationality" })}

              {renderInput({ label: "Address Line 1", name: "address1" })}
              {renderInput({ label: "Address Line 2", name: "address2" })}

              {renderInput({
                label: "Postal Code",
                name: "postalCode",
                required: true,
                onBlur: handlePostalBlur
              })}

              {renderInput({
                label: "City",
                name: "city",
                required: true,
                disabled: false
              })}

              {renderInput({
                label: "State",
                name: "state",
                disabled: false
              })}

              {renderCountryPicker()}
              
              
              {renderInput({
                label: "Emergency Contact Name",
                name: "emergencyContactName"
              })}

              {renderPhone({
                label: "Emergency Contact Phone",
                name: "emergencyContactPhone"
              })}

              {renderSelect({
                label: "Relationship",
                name: "relationship",
                options: ["Parent", "Spouse", "Sibling", "Friend", "Other"]
              })}

                {renderSelect({
                label: "Blood Group",
                name: "bloodGroup",
                options: [
                    "A+",
                    "A-",
                    "B+",
                    "B-",
                    "AB+",
                    "AB-",
                    "O+",
                    "O-",
                    "Unknown"
                ]
                })}
                {renderInput({
                label: "Family Doctor Details",
                name: "familyDoctorDetails"
                 })}
              {renderInput({
                label: "Any Medical History",
                name:  "medicalHistory"
                })}

            </div>
          </div>

          {/* ================================================= */}
          {/* ✅ QUALIFICATION */}
          {/* ================================================= */}

          <div className="profile-card">
            <div className="card-header">
              <h2>Qualification & Experience</h2>
            </div>

            <div className="form-grid">
              {renderInput({
                label: "Highest Qualification",
                name: "qualification",
                required: true
              })}

              {renderInput({
                label: "University / Institute",
                name: "institute"
              })}

              {renderInput({
                label: "Graduation Year",
                name: "graduationYear",
                type: "number"
              })}

              {renderInput({
                label: "Total Experience",
                name: "experience",
                type: "number",
                required: true
              })}

              {renderInput({
                label: "Primary Domain",
                name: "domain"
              })}

              {renderInput({
                label: "Skills",
                name: "skills"
              })}
              
              {renderInput({
                label: "Last Designation",
                name: "lastDesignation"
              })}

              {renderInput({
                label: "Last Company",
                name: "lastCompany"
              })}

              {renderInput({ 
                label: "Last Relieving Date", 
                name: "lastRelievingDate", 
                type: "date" 
              })}

              

              {renderTextarea({
                label: "Qualification Summary",
                name: "qualificationSummary"
              })}

              {renderTextarea({
                label: "Experience Summary",
                name: "experienceSummary",
                required: true
              })}

              {renderTextarea({
                label: "Key Achievements",
                name: "achievements"
              })}

              {renderTextarea({
                label: "Certifications",
                name: "certifications"
              })}

              {renderTextarea({
                label: "Trainings Completed",
                name: "trainingsCompleted"
              })}

              {renderTextarea({
                label: "Awards & Recognitions",
                name: "awards"
              })}

              {renderTextarea({
                label: "Performance Summary",
                name: "performanceSummary"
              })}

              {renderInput({
                label: "LinkedIn Profile URL",
                name: "linkedin"
              })}

              {renderInput({
                label: "GitHub URL",
                name: "github"
              })}

              {renderInput({
                label: "Portfolio URL",
                name: "portfolio"
              })}
            </div>
          </div>

          {/* ================================================= */}
          {/* ✅ ACCOUNT DETAILS */}
          {/* ================================================= */}

          <div className="profile-card">
            <div className="card-header">
              <h2>Account Details (Optional)</h2>
            </div>

            <div className="form-grid">
              {renderInput({
                label: "Bank Name",
                name: "bankName"
              })}

              {renderInput({
                label: "Account Holder Name",
                name: "accountHolder"
              })}

              {renderAccountNumber()}

              {renderInput({
                label: "IFSC Code",
                name: "ifsc"
              })}

              {renderInput({
                label: "IBAN",
                name: "iban"
              })}

              {renderInput({
                label: "SWIFT / BIC",
                name: "swift"
              })}
            </div>
          </div>

          {/* ================================================= */}
          {/* ✅ COMMUNITY */}
          {/* ================================================= */}

          <div className="profile-card">
            <div className="card-header between">
              <h2>Community & Interests (Optional)</h2>

              <select
                name="visibility"
                className="privacy-select"
                value={profileData.visibility}
                onChange={handleChange}
                disabled={!canEditProfile}
              >
                <option>Only Me</option>
                <option>Me + HR</option>
                <option>Me + HR + Manager</option>
                <option>Everyone</option>
              </select>
            </div>

            <div className="community-note">
              Optional information. Not used directly for performance rating.
            </div>

            {renderTextarea({
              label: "Community Activities",
              name: "community"
            })}
          </div>
        </div>

      </div>
      {/* ✅ CLOSES profile-layout */}

      {/* ================================================= */}
      {/* ✅ ACTION BAR */}
      {/* ================================================= */}
      <div className="action-bar">
        <div className="left-actions">
          <button className="secondary-btn" onClick={() => setShowAttachmentModal(true)} disabled={!canEditProfile}>Upload Attachments</button>
          <button className="secondary-btn" onClick={() => setShowResumeModal(true)} disabled={!canEditProfile}>Upload Resume</button>
        </div>
        <div className="right-actions">
          <button className="ghost-btn" onClick={handleRefresh}>Refresh</button>
          <button className="secondary-btn" onClick={handleOpenMyCTC} disabled={loadingMyCTC || !profileData.employeeId}>{loadingMyCTC ? "Loading CTC..." : "My CTC"}</button>
          {!editMode && !isProfileLocked && (<button className="secondary-btn" onClick={() => setEditMode(true)}>Edit</button>)}
          {!editMode && isProfileLocked && (<button className="secondary-btn" disabled title="Profile is locked because it is pending verification or verified.">Profile Locked</button>)}
          {editMode && (<button className="primary-btn" onClick={handleSave} disabled={isProfileLocked}>Save</button>)}
          {canSubmitProfile && (<button className="primary-btn" onClick={handleSubmitProfile}>Submit Profile</button>)}
        </div>
      </div>

      {showMyCTCPanel && renderMyCTCSection()}

      {/* ================================================= */}
      {/* ✅ ATTACHMENT MODAL */}
      {/* ================================================= */}

      {showAttachmentModal && (

        <div className="modal-overlay">

          <div className="modal-box">

            <h3>Upload Attachments</h3>

            <input
              type="file" 
              multiple 
              onChange={handleAttachmentUpload} 
              disabled={!canEditProfile}
            />

            <div className="modal-actions">

              <button
                className="secondary-btn"
                onClick={() => setShowAttachmentModal(false)}
              >
                Close
              </button>

            </div>

          </div>

        </div>
      )}

      {/* ================================================= */}
      {/* ✅ RESUME MODAL */}
      {/* ================================================= */}

     {showResumeModal && (
  <div className="modal-overlay">
    <div className="modal-box">
      <h3>Upload Resume</h3>

      <p style={{ marginTop: 0, color: "#64748b", fontSize: "14px" }}>
        Upload a PDF or DOCX resume. The local Python API will extract available profile details.
      </p>

      <input
        type="file"
        accept=".pdf,.docx"
        onChange={handleResumeFileChange}
        disabled={!canEditProfile}
      />

      {resumeFile && (
        <p style={{ fontSize: "13px", marginTop: "10px" }}>
          Selected: <strong>{resumeFile.name}</strong>
        </p>
      )}

      <div className="modal-actions">
        <button
          className="ghost-btn"
          onClick={() => {
            setShowResumeModal(false);
            setResumeFile(null);
          }}
          disabled={extractingResume}
        >
          Cancel
        </button>

        <button
          className="primary-btn"
          onClick={handleExtractResume}
          disabled={extractingResume}
        >
          {extractingResume ? "Extracting..." : "Extract Resume"}
        </button>
      </div>
      </div>
     </div>
    )}

    {showExtractReviewModal && extractedResumeData && (
  <div className="modal-overlay">
    <div className="modal-box extract-review-modal">
      <h3>Review Extracted Resume Data</h3>

      <p style={{ marginTop: 0, color: "#64748b", fontSize: "14px" }}>
        Please review extracted data before applying it to the profile form.
      </p>

      <div className="extract-review-list">
        {Object.entries(extractedResumeData).map(([key, value]) => (
          <div key={key} className="extract-review-item">
            <label>{key}</label>
            <div>{value || "-"}</div>
          </div>
        ))}
      </div>

      <div className="modal-actions">
        <button
          className="ghost-btn"
          onClick={cancelExtractedResumeData}
        >
          Cancel
        </button>

        <button
          className="primary-btn"
          onClick={applyExtractedResumeData}
        >
          Apply to Profile
        </button>
        </div>
      </div>
    </div>
   )}
    </div>
  );

}


const profileMiniCardStyle = {
  border: "1px solid #e5edf6",
  borderRadius: "14px",
  padding: "14px",
  background: "#ffffff",
  boxShadow: "0 3px 13px rgba(15,23,42,0.052)",
};

const profileMiniTitleStyle = {
  margin: "0 0 12px",
  fontSize: "15px",
  color: "#172033",
  borderBottom: "1px solid #e5e7eb",
  paddingBottom: "8px",
};

export default Profile;